import { defineRouting } from "next-intl/routing";

/**
 * The cookie next-intl's middleware reads the visitor's language from.
 * Exported so a link that switches language can write the same cookie the
 * middleware will read (`components/radar/RadarHubLink.tsx`).
 */
export const LOCALE_COOKIE = {
  name: "NEXT_LOCALE",
  maxAge: 31536000, // 1 year
  sameSite: "lax",
} as const;

export const routing = defineRouting({
  // All supported locales
  locales: ["en-US", "pt-br"],

  // Default locale (English, United States)
  defaultLocale: "en-US",

  // Locale prefix strategy:
  // 'as-needed' hides the default locale prefix (/about for en-US, /pt-br/about for pt-br)
  localePrefix: "as-needed",

  // Locale detection cookie settings
  localeCookie: LOCALE_COOKIE,
});

export type Locale = (typeof routing.locales)[number];

/**
 * The site path of `path` in `locale`, under the `as-needed` rule above: no
 * prefix for the default locale, `/pt-br/...` for the others.
 *
 * The one place that rule is written. next-intl's `Link` cannot be told it —
 * an explicit `locale` prop always prefixes, so the default locale came out as
 * `/en-US/...`, a 307 to the canonical URL (#3387) — so a link that names the
 * language of its target builds the href here instead. Empty for the default
 * locale's own root, which is why `buildLocalizedUrl` puts the origin in front.
 */
export function localePath(locale: Locale, path: string): string {
  const prefix = locale === routing.defaultLocale ? "" : `/${locale}`;
  const normalized =
    !path || path === "/" ? "" : path.startsWith("/") ? path : `/${path}`;
  return `${prefix}${normalized}`;
}

/** The prefix alone: `""` for the default locale, `/pt-br` for the others. */
export function localePathPrefix(locale: string): string {
  return localePath(locale as Locale, "/");
}
